import _ from "lodash";
import React from "react";
import toast from "react-hot-toast";
import ThemedModal from "../../../utilities/ThemedModal";
import EmailMessageForm from "../../../forms/emails/EmailMessageForm";
import RestrictedRecipientMessage from "./RestrictedRecipientMessage";
import { useForm } from "react-hook-form";
import { useAuth } from "react-oidc-context";
import { useMailMessageForm } from "../../../../hooks/useMailMessageForm";
import { Button } from "../../../";
import { Modal } from "react-bootstrap";
import { EmailPopoutModalProps } from "../types";
import { toNString } from "../../../../helpers/guids";
import { openEmailSaveChangesConfirmation } from "../../email-save";
import { openEmailRestrictedFileConfirmation, RestrictedEmailConfirmationResponse } from "../../email-restricted-confirm";

export const EmailPopoutModal = ({
    backdrop = 'static',
    cancel,
    centered = true,
    defaultValues,
    dialogId,
    dismiss,
    isAttachmentsShownFirst,
    isDocumentSelectorShown,
    isForward,
    isReply,
    mailType,
    proceed,
    show,
    size = "xl",
    ...rest
} : Readonly<EmailPopoutModalProps>) => {
    const { user } = useAuth();
    const mailForm = useForm<MailMessage>({ defaultValues: defaultValues ?? {
        parentMailMessageId: null,
        masterAccountId: null,
        projectId: null,
        subject: '',
        body: '',
        from: toNString(user!.profile.sub),
        attachments: [],
        toRecipients: [],
        ccRecipients: [],
        bccRecipients: []
    }});
    const { isBusy, isDirty, isRestricted, saveDraft, sendMail } = useMailMessageForm(mailType);
    const currentForm = mailForm.watch();
    const hasRestrictedRecipients = React.useMemo(() => isRestricted(currentForm), [ currentForm ]);

    const onClose = async () : Promise<void> => {
        // NOTE: we have to force validation to mark fields as dirty
        const isValid = await mailForm.trigger();
        if (!isValid) {
            dismiss!();
            return;
        }

        // we want to check if the form is "dirty" - basically its changed, then we notify/ask if they want to quit out the changes
        if (isDirty(mailForm.formState)) {
            const isSaveChanges = await openEmailSaveChangesConfirmation(currentForm);
            if (isSaveChanges) {
                const saveResult = await toast.promise(saveDraft(currentForm), {
                    loading: 'Saving draft...',
                    success: 'Draft saved!',
                    error: 'There was a problem saving your draft. The process did not complete.'
                });

                if (!saveResult) {
                    return;
                }
            }
        }

        dismiss!();
    };

    const onSaveDraft = async () : Promise<void> => {
        // force validation before we can allow saving
        const isValid = await mailForm.trigger();
        if (!isValid) {
            return;
        }

        const draftResponse = await toast.promise(saveDraft(currentForm), {
            loading: 'Saving draft...',
            success: 'Draft saved!',
            error: 'There was a problem saving your draft. The process did not complete.'
        });

        if (draftResponse?.id) {
            mailForm.setValue('mailMessageId', draftResponse.id);
            proceed!(currentForm);
        }
    };

    const onSubmit = async (form: MailMessage) : Promise<void> => {
        try {
            // check if we are sending to a restricted email, which means we want to warn/get-confirmation
            const confirmation: RestrictedEmailConfirmationResponse = hasRestrictedRecipients
                ? await openEmailRestrictedFileConfirmation(form)
                : 'send-file'; // this will be the default to just automatically send and file to the master-account (if the master-account-id is set of course)

            // check we are cancelling the send request
            if (confirmation === 'cancel') {
                return;
            }

            // save the draft first, this gets the graph-id that the send uses
            const draftEmail = await saveDraft(form);
            if (!draftEmail?.id) {
                return;
            }

            // makes the request to send the newly generated draft
            mailForm.setValue('mailMessageId', draftEmail.id);
            await toast.promise(sendMail(draftEmail.id, form.from, form.masterAccountId, confirmation === 'send-file'), {
                loading: 'Sending email...',
                success: 'Email sent!',
                error: 'There was a problem sending your email. The process did not complete.'
            });

            // this closes the modal once the send has finished!
            proceed!(form);
        }
        catch (err: any) {
            toast.error(err?.message ?? 'An unknown error occured whilst trying to send the email.')
        }
    };

    return (
        <Modal className="overflow-visible" backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header>
                <Modal.Title>
                    {mailType === 'forward'
                        ? <span>Forward Email</span>
                        : <span>Create {mailType === 'new' ? "Email" : "Reply"}</span>
                    }
                </Modal.Title>
            </ThemedModal.Header>
            <form onSubmit={mailForm.handleSubmit(onSubmit)}>
                <Modal.Body className="p-0">
                    {hasRestrictedRecipients && (<RestrictedRecipientMessage />)}
                    <section className="p-3">
                        <EmailMessageForm
                            defaultValues={defaultValues}
                            isAttachmentsShownFirst={isAttachmentsShownFirst}
                            isDocumentSelectorShown={isDocumentSelectorShown}
                            isForward={isForward}
                            isReply={isReply}
                            {...mailForm}
                        />
                    </section>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" variant="secondary" disabled={isBusy} onClick={onSaveDraft}>Save as Draft</Button>
                    <Button type="submit" variant="primary" disabled={isBusy}>Save & Send Email</Button>
                    <Button type="button" variant="light" disabled={isBusy} onClick={onClose}>Close</Button>
                </Modal.Footer>
            </form>
        </Modal>
    )
};